/**
 * ICE Binder API Contract
 *
 * Defines the oRPC contract for the ICE Binder feature — pre-export health checks,
 * document binder generation, status polling, and secure download for immigration compliance.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
// ============ Response Schemas ============
const healthCheckResultSchema = z.object({
    locationId: z.uuid(),
    locationName: z.string(),
    employeeCount: z.number().int().nonnegative(),
    missingDocumentCount: z.number().int().nonnegative(),
    issues: z.array(z.string()),
    status: z.enum(["ok", "warning", "error"]),
});
const healthCheckResponseSchema = z.object({
    locations: z.array(healthCheckResultSchema),
    allPassed: z.boolean(),
});
const jobStatusSchema = z.enum([
    "PENDING",
    "PROCESSING",
    "COMPLETED",
    "FAILED",
]);
// ============ Contract Definitions ============
// Pre-export health check — validates ICE binder readiness for given locations
const healthCheck = oc
    .route({
    method: "POST",
    path: "/ice-binder/health-check",
    summary: "Run pre-export health check for ICE binder",
    tags: ["ICE Binder"],
})
    .input(z.object({
    locationIds: z.array(z.uuid()).min(1).max(100),
}))
    .output(healthCheckResponseSchema);
// Generate ICE binder — initiates async generation and returns a job ID
const generate = oc
    .route({
    method: "POST",
    path: "/ice-binder/generate",
    summary: "Generate ICE binder document",
    tags: ["ICE Binder"],
    successStatus: 202,
})
    .input(z.object({
    locationIds: z.array(z.uuid()).min(1).max(100),
    disclaimerAccepted: z.boolean(),
}))
    .output(z.object({
    jobId: z.string(),
}));
// Poll job status — returns current processing state for a generation job
const status = oc
    .route({
    method: "GET",
    path: "/ice-binder/status/{jobId}",
    summary: "Get ICE binder generation job status",
    tags: ["ICE Binder"],
})
    .input(z.object({
    jobId: z.uuid(),
}))
    .output(z.object({
    status: jobStatusSchema,
    pageCount: z.number().int().optional(),
    fileSizeBytes: z.number().int().optional(),
    error: z.string().optional(),
}));
// Download completed binder — returns a presigned URL for the generated PDF
const download = oc
    .route({
    method: "GET",
    path: "/ice-binder/download/{jobId}",
    summary: "Download generated ICE binder PDF",
    tags: ["ICE Binder"],
})
    .input(z.object({
    jobId: z.uuid(),
}))
    .output(z.object({
    downloadUrl: z.string(),
}));
// Per-employee compliance detail for a single location — paginated
const locationCompliance = oc
    .route({
    method: "GET",
    path: "/ice-binder/location/{locationId}/compliance",
    summary: "Get per-employee compliance detail for a location",
    tags: ["ICE Binder"],
})
    .input(z.object({
    locationId: z.uuid(),
    page: z.number().int().min(1).default(1),
    limit: z.number().int().min(1).max(100).default(50),
}))
    .output(z.object({
    employees: z.array(z.object({
        name: z.string(),
        hasMissingDocs: z.boolean(),
    })),
    total: z.number().int().nonnegative(),
    page: z.number().int(),
    limit: z.number().int(),
}));
// ============ Export Contract ============
export const iceBinderContract = {
    healthCheck,
    generate,
    status,
    download,
    locationCompliance,
};
//# sourceMappingURL=iceBinder.js.map